/* eslint-disable react/no-danger */
import React, { useState } from 'react';
import moment from 'moment';
import ImminenceDate from 'components/atoms/imminence_date';
import ClipboardIcon from 'components/atoms/clipboard_icon';
import PhoneCard from 'components/atoms/phone_card';
import Loader from 'components/atoms/loader';
import AiManagementBiddingItems from 'components/molecules/ai_management_bidding_items';
import { BiddingData } from 'store/modules/bidding_store/types';
import iconClock from 'assets/images/icon-clock.svg';
import { Container } from './styles';

interface AiManagementBiddingInfoProps {
  bidding: BiddingData | null;
  loading: boolean;
  error: boolean;
}

const AiManagementBiddingInfo: React.FC<AiManagementBiddingInfoProps> = ({
  bidding,
  loading,
  error,
}) => {
  const [statusTwoDays, setStatusTwoDays] = useState(false);
  const [statusOneDay, setStatusOneDay] = useState(false);
  const [statusDay, setStatusDay] = useState(false);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Loader />
      </div>
    );
  }

  if (error || !bidding) {
    return (
      <div className="alert alert-warning" role="alert">
        Não foi possível carregar as informações da licitação.
      </div>
    );
  }

  const situation = bidding.bidding_grouping?.descricao;
  const sessionStatus = bidding.status_bidding_info?.[0]?.status_name;
  const portalUrl = bidding.status_bidding_info?.at(-1)?.url_portal;
  const hasDates =
    bidding.datahora_abertura ||
    bidding.datahora_prazo ||
    bidding.datahora_documento ||
    bidding.datahora_retirada ||
    bidding.datahora_visita;

  return (
    <Container>
      <div className="card border p-4">
        {(situation || sessionStatus) && (
          <div className="d-flex flex-wrap align-items-center mb-3">
            {situation && (
              <span
                className={`rounded mr-2 mb-1 bidding-situation-${situation
                  .replace(/\s+/g, '-')
                  .toLowerCase()}`}
              >
                {situation}
              </span>
            )}
            {sessionStatus && (
              <span className="session-status rounded mr-2 mb-1">{sessionStatus}</span>
            )}
          </div>
        )}

        <div className="d-flex mb-2 bidding-info">
          <div className="bidding-info-title pr-2 text-nowrap">Objeto:</div>
          <div
            className="flex-grow-1"
            dangerouslySetInnerHTML={{ __html: bidding.objeto || '-' }}
          />
        </div>
        <hr />

        <div className="row bidding-info mb-2">
          <div className="col-md-6 d-flex align-items-center mb-2 mb-md-0">
            <div className="bidding-info-title mr-2 text-nowrap">Datas:</div>
            <div className="text-secondary d-flex align-items-center flex-wrap">
              <span
                className={`${statusTwoDays || statusOneDay ? 'notice-date px-1 text-dark' : ''} ${
                  statusDay ? 'bg-danger px-1 text-white' : ''
                }`}
              >
                {!hasDates && 'Não Informadas'}
                {bidding.datahora_abertura && (
                  <ImminenceDate
                    date={bidding.datahora_abertura}
                    dateType="Abertura"
                    setStatusTwoDays={setStatusTwoDays}
                    setStatusOneDay={setStatusOneDay}
                    setStatusDay={setStatusDay}
                  />
                )}
                {bidding.datahora_prazo && (
                  <ImminenceDate
                    date={bidding.datahora_prazo}
                    dateType="Prazo"
                    setStatusTwoDays={setStatusTwoDays}
                    setStatusOneDay={setStatusOneDay}
                    setStatusDay={setStatusDay}
                  />
                )}
                {bidding.datahora_documento && (
                  <ImminenceDate
                    date={bidding.datahora_documento}
                    dateType="Documento"
                    setStatusTwoDays={setStatusTwoDays}
                    setStatusOneDay={setStatusOneDay}
                    setStatusDay={setStatusDay}
                  />
                )}
                {bidding.datahora_retirada && (
                  <ImminenceDate
                    date={bidding.datahora_retirada}
                    dateType="Retirada"
                    setStatusTwoDays={setStatusTwoDays}
                    setStatusOneDay={setStatusOneDay}
                    setStatusDay={setStatusDay}
                  />
                )}
                {bidding.datahora_visita && (
                  <ImminenceDate
                    date={bidding.datahora_visita}
                    dateType="Visita"
                    setStatusTwoDays={setStatusTwoDays}
                    setStatusOneDay={setStatusOneDay}
                    setStatusDay={setStatusDay}
                  />
                )}
              </span>
              {statusTwoDays && <span className="ml-2"> 2 dias para encerrar</span>}
              {statusOneDay && <span className="ml-2"> 1 dia para encerrar</span>}
              {statusDay && <span className="text-danger ml-2"> VENCIMENTO HOJE</span>}
              {(statusTwoDays || statusOneDay) && (
                <img src={iconClock} alt="ícone de relógio" className="ml-2 mb-1" />
              )}
            </div>
          </div>
          <div className="col-md-6 d-flex">
            <div className="bidding-info-title mr-2 text-nowrap">Órgão:</div>
            <div className="text-secondary">
              {!bidding.orgao_uasg && !bidding.public_body?.nome && '-'}
              {bidding.orgao_uasg}
              {bidding.orgao_uasg ? ' - ' : ''}
              {bidding.public_body?.nome}
            </div>
          </div>
        </div>

        <div className="row bidding-info mb-2">
          <div className="col-md-6 d-flex mb-2 mb-md-0">
            <div className="bidding-info-title mr-2 text-nowrap">Cidade:</div>
            <div
              className="text-secondary"
              dangerouslySetInnerHTML={{
                __html: `${bidding.orgao_cidade} - ${bidding.orgao_estado}`,
              }}
            />
          </div>
          <div className="col-md-6 d-flex">
            <div className="bidding-info-title mr-2 text-nowrap">Edital:</div>
            <div
              className="text-secondary"
              dangerouslySetInnerHTML={{ __html: `${bidding.edital || '-'}` }}
            />
          </div>
        </div>

        <div className="row bidding-info mb-2">
          {bidding.valor_estimado > 0 && (
            <div className="col-md-6 d-flex align-items-center mb-2 mb-md-0">
              <div className="bidding-info-title mr-2 text-nowrap">Valor estimado:</div>
              <span className="estimated font-weight-bold">
                {bidding.valor_estimado.toLocaleString('pt-br', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </span>
            </div>
          )}
          {portalUrl && (
            <div className="col-md-6 d-flex">
              <div className="bidding-info-title mr-2 text-nowrap">Link:</div>
              <a href={portalUrl} target="_blank" rel="noopener noreferrer">
                Acessar local da disputa
              </a>
            </div>
          )}
        </div>

        <div className="row bidding-info mb-2">
          <div className="col-12 d-flex">
            <div className="bidding-info-title mr-2 text-nowrap">Observações:</div>
            <div className="text-secondary">{bidding.observacao || 'Sem Observação'}</div>
          </div>
        </div>

        <div className="row bidding-info mb-2">
          <div className="col-md-6 d-flex mb-2 mb-md-0">
            <div className="bidding-info-title mr-2 text-nowrap">Processo:</div>
            <div
              className="text-secondary"
              dangerouslySetInnerHTML={{ __html: bidding.processo || '-' }}
            />
          </div>
          <div className="col-md-6 d-flex">
            <div className="bidding-info-title mr-2 text-nowrap">Telefone:</div>
            <div className="text-secondary">
              {bidding.phones?.length > 0 ? <PhoneCard phones={bidding.phones} /> : '--'}
            </div>
          </div>
        </div>

        {bidding.edital_homepage1 && (
          <div className="row bidding-info mb-2">
            <div className="col-12 d-flex">
              <div className="bidding-info-title mr-2 text-nowrap">Site:</div>
              <a
                href={
                  bidding.edital_homepage1.includes('http')
                    ? bidding.edital_homepage1
                    : `http://${bidding.edital_homepage1}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="break-text"
              >
                {bidding.edital_homepage1}
              </a>
            </div>
          </div>
        )}

        <hr />

        <div className="meta d-flex justify-content-between align-items-center text-secondary flex-column flex-md-row">
          <div>
            <span className="number-cnl font-weight-bold">Nº Conlicitação:</span>{' '}
            <span>{bidding.bidding_id}</span>
            <ClipboardIcon
              value={bidding.bidding_id?.toString() ?? '-'}
              action="Gestão IA - Copiar número conlicitação"
            />
          </div>
          <div>
            <span>Atualizada em: </span>
            <span>{moment(bidding.modified || '').format('L [às] LT')}</span>
          </div>
        </div>
      </div>

      {bidding.itens_json && bidding.itens_json.length > 0 && (
        <AiManagementBiddingItems items={bidding.itens_json} />
      )}
    </Container>
  );
};

export default AiManagementBiddingInfo;
